import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  FiPackage, FiUsers, FiDollarSign, FiTrendingUp, 
  FiClock, FiCheckCircle, FiAlertCircle, FiArrowRight,
  FiHome, FiCalendar
} from 'react-icons/fi';
import { shedService } from '../../services/shedService';
import { dealerService } from '../../services/dealerService';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';

const AdminDashboardShed = ({ setActiveTab }) => {
  const { userRole, dealerId } = useAuth();
  const isMaster = userRole === 'master_admin';
  const [stats, setStats] = useState({
    totalSheds: 0,
    availableSheds: 0,
    soldSheds: 0,
    totalValue: 0,
    totalLeads: 0,
    pendingLeads: 0,
    recentPayments: 0,
    monthlyRevenue: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [recentSheds, setRecentSheds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load sheds based on role
      let allShedsResult, availableShedsResult;
      if (isMaster) {
        allShedsResult = await shedService.getAllSheds();
        availableShedsResult = await shedService.getAvailableSheds();
      } else if (dealerId) {
        allShedsResult = await shedService.getShedsByDealer(dealerId);
        const dealerSheds = allShedsResult.data || [];
        availableShedsResult = { data: dealerSheds.filter(s => s.status !== 'sold'), error: null };
      } else {
        allShedsResult = { data: [], error: null };
        availableShedsResult = { data: [], error: null };
      }
      const allSheds = allShedsResult.data;
      const availableSheds = availableShedsResult.data;
      
      // Calculate stats
      const soldCount = (allSheds || []).filter(s => s.status === 'sold').length;
      const totalValue = (availableSheds || []).reduce((sum, s) => sum + (s.salePrice || s.price || 0), 0);
      
      // Load leads
      let leadsQuery = supabase
        .from('leads')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(5);
      if (!isMaster && dealerId) {
        leadsQuery = leadsQuery.eq('dealer_id', dealerId);
      }
      const { data: leads } = await leadsQuery;
      
      const pendingLeads = (leads || []).filter(l => l.status === 'new' || l.status === 'pending').length;
      
      // Load recent payments
      let paymentsQuery = supabase
        .from('payments')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(10);
      if (!isMaster && dealerId) {
        paymentsQuery = paymentsQuery.eq('dealer_id', dealerId);
      }
      const { data: payments } = await paymentsQuery;
      
      const monthStart = new Date();
      monthStart.setDate(1);
      monthStart.setHours(0, 0, 0, 0);
      
      const monthlyRevenue = (payments || [])
        .filter(p => new Date(p.created_at) >= monthStart)
        .reduce((sum, p) => sum + (p.amount || 0), 0);

      setStats({
        totalSheds: (allSheds || []).length,
        availableSheds: (availableSheds || []).length,
        soldSheds: soldCount,
        totalValue,
        totalLeads: (leads || []).length,
        pendingLeads,
        recentPayments: (payments || []).length,
        monthlyRevenue
      });
      
      setRecentLeads(leads || []);
      setRecentSheds((availableSheds || []).slice(0, 5));
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const statCards = [
    { 
      title: 'Total Sheds', 
      value: stats.totalSheds, 
      icon: FiPackage, 
      color: 'emerald',
      onClick: () => setActiveTab('sheds')
    },
    { 
      title: 'Available', 
      value: stats.availableSheds, 
      icon: FiCheckCircle, 
      color: 'green',
      onClick: () => setActiveTab('sheds')
    },
    { 
      title: 'Inventory Value', 
      value: formatPrice(stats.totalValue), 
      icon: FiDollarSign, 
      color: 'blue',
      onClick: () => setActiveTab('sheds')
    },
    { 
      title: 'Pending Leads', 
      value: stats.pendingLeads, 
      icon: FiUsers, 
      color: 'orange',
      onClick: () => setActiveTab('leads')
    },
    { 
      title: 'Monthly Revenue', 
      value: formatPrice(stats.monthlyRevenue), 
      icon: FiTrendingUp, 
      color: 'purple',
      onClick: () => setActiveTab('payments')
    },
    { 
      title: 'Sold This Month', 
      value: stats.soldSheds, 
      icon: FiHome, 
      color: 'teal',
      onClick: () => setActiveTab('sheds')
    },
  ];

  const getColorClasses = (color) => {
    const colors = {
      emerald: 'bg-emerald-50 text-emerald-600 border-emerald-200',
      green: 'bg-green-50 text-green-600 border-green-200',
      blue: 'bg-blue-50 text-blue-600 border-blue-200',
      orange: 'bg-orange-50 text-orange-600 border-orange-200',
      purple: 'bg-purple-50 text-purple-600 border-purple-200',
      teal: 'bg-teal-50 text-teal-600 border-teal-200',
    };
    return colors[color] || colors.emerald;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl h-32 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
        <p className="text-gray-600 dark:text-gray-400">Welcome to ShedX Admin</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.button
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            onClick={stat.onClick}
            className="bg-white dark:bg-slate-800 rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow text-left w-full"
          >
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg border ${getColorClasses(stat.color)}`}>
                <stat.icon className="w-6 h-6" />
              </div>
              <FiArrowRight className="w-5 h-5 text-gray-400" />
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">{stat.title}</div>
          </motion.button>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Leads */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Leads</h2>
            <button
              onClick={() => setActiveTab('leads')}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1"
            >
              View All <FiArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {recentLeads.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No recent leads</div>
            ) : (
              recentLeads.map((lead) => (
                <div key={lead.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {lead.first_name} {lead.last_name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {lead.email || lead.phone}
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                        lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                        lead.status === 'qualified' ? 'bg-green-100 text-green-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {lead.status || 'new'}
                      </span>
                      <div className="text-xs text-gray-400 mt-1">
                        {formatDate(lead.created_at)}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>

        {/* Recent Sheds */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden"
        >
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Inventory</h2>
            <button
              onClick={() => setActiveTab('sheds')}
              className="text-emerald-600 hover:text-emerald-700 text-sm font-medium flex items-center gap-1"
            >
              View All <FiArrowRight className="w-4 h-4" />
            </button>
          </div>
          <div className="divide-y divide-gray-100 dark:divide-slate-700">
            {recentSheds.length === 0 ? (
              <div className="p-6 text-center text-gray-500">No sheds in inventory</div>
            ) : (
              recentSheds.map((shed) => (
                <div key={shed.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-slate-700 rounded-lg overflow-hidden flex-shrink-0">
                      {shed.images?.[0] ? (
                        <img src={shed.images[0]} alt={shed.name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <FiHome className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 dark:text-white truncate">
                        {shed.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {shed.size} • {shed.shedType}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-emerald-600">
                        {formatPrice(shed.salePrice || shed.price)}
                      </div>
                      <span className={`inline-block px-2 py-0.5 text-xs rounded ${
                        shed.status === 'available' ? 'bg-green-100 text-green-700' :
                        shed.status === 'reserved' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {shed.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </motion.div>
      </div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6"
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <button
            onClick={() => setActiveTab('sheds')}
            className="flex flex-col items-center gap-2 p-4 bg-emerald-50 dark:bg-emerald-900/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/50 rounded-lg transition-colors"
          >
            <FiPackage className="w-6 h-6 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700 dark:text-emerald-400">Add Shed</span>
          </button>
          <button
            onClick={() => setActiveTab('leads')}
            className="flex flex-col items-center gap-2 p-4 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-lg transition-colors"
          >
            <FiUsers className="w-6 h-6 text-blue-600" />
            <span className="text-sm font-medium text-blue-700 dark:text-blue-400">View Leads</span>
          </button>
          <button
            onClick={() => setActiveTab('payments')}
            className="flex flex-col items-center gap-2 p-4 bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 rounded-lg transition-colors"
          >
            <FiDollarSign className="w-6 h-6 text-purple-600" />
            <span className="text-sm font-medium text-purple-700 dark:text-purple-400">Payments</span>
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className="flex flex-col items-center gap-2 p-4 bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50 rounded-lg transition-colors"
          >
            <FiTrendingUp className="w-6 h-6 text-orange-600" />
            <span className="text-sm font-medium text-orange-700 dark:text-orange-400">Analytics</span>
          </button>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminDashboardShed;
